/* eslint-disable no-underscore-dangle */
// VaultTile.tsx
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    IconButton,
    LinearProgress,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useState } from 'react';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { VaultMediaResponseList, VaultMediaSettings } from '../../hooks/useSubscriberVaultMedias';
import useSubscribersVaultMediasAdditonal from '../../hooks/useSubscriberVaultMediasAdditional';
import { useDownloadManager } from '../../store/downloadManager/useDownloadManager';
import DialogAdaptive from '../common/DialogAdaptive';
import MediaLightbox from '../common/MediaLightbox';
import StyledCardTitleBar from '../common/StyledCardTitleBar';

interface VaultTileProps {
    subscriber: OnlyFansSubscriber;
    item: VaultMediaResponseList;
    isSelected: boolean;
    width?: number; // default 150
    height?: number; // default 150
    allowDownload?: boolean;
    selectedItems?: VaultMediaResponseList[];
    setSelectedItems?: (item: VaultMediaResponseList[]) => void;
    handleMediaClicked: (item: VaultMediaResponseList) => void;
    // handleEditMenuOpen?: (item: VaultMediaResponseList) => void;
}

/* ---------- helpers ---------- */

const formatDuration = (sec: number) => {
    const s = (sec % 60).toString().padStart(2, '0');
    const mTotal = Math.floor(sec / 60);
    const m = (mTotal % 60).toString().padStart(2, '0');
    const h = Math.floor(mTotal / 60);
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const formatDate = (d: Date) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/* ---------- component ---------- */

const VaultTile: React.FC<VaultTileProps> = ({
    subscriber,
    item,
    isSelected,
    width = 150,
    height = 150,
    allowDownload,
    handleMediaClicked,
}: // handleEditMenuOpen,
VaultTileProps) => {
    const theme = useTheme();
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();

    const showImage = item.type !== 'audio';
    const imgSrc = item.cachedThumb || item.thumb || '';
    const durText = item.type === 'video' || item.type === 'audio' || item.type === 'gif' ? formatDuration(item.duration ?? 0) : '';
    const isPhoto = item.type === 'photo';
    const stop = (e: React.MouseEvent | React.TouchEvent) => e.stopPropagation();

    const { current, isDownloading, progress, addToQueue } = useDownloadManager();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [openEdit, setOpenEdit] = React.useState(false);
    const [title, setTitle] = React.useState(item.settings?.title ?? '');
    const [notes, setNotes] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [editItem, setEditItem] = useState<VaultMediaResponseList>();
    const [saveSettingsIsloading, setSaveSettingsIsLoading] = useState<boolean>(false);
    const { updateVaultMediaSettings, updateVaultMediaInCache } = useSubscribersVaultMediasAdditonal();

    const onSaveSettings = React.useCallback(
        async (
            id: number,
            draft: {
                title: string;
                description: string;
                notes: string;
            },
        ) => {
            const newSettings: VaultMediaSettings = {
                title: draft.title.trim(),
                description: draft.description.trim(),
                notes: draft.notes.trim(),
                updatedAt: new Date(),
            };

            setSaveSettingsIsLoading(true);
            const ok = await updateVaultMediaSettings(id, newSettings);
            if (ok) {
                updateVaultMediaInCache(id, curr => ({ ...curr, settings: newSettings }));
                setOpenEdit(false);
            }
            setSaveSettingsIsLoading(false);
        },
        [updateVaultMediaSettings, updateVaultMediaInCache],
    );
    const handleEditMenuOpen = React.useCallback(
        (item: VaultMediaResponseList) => {
            setOpenEdit(true);
            setEditItem(item);
            // setTitle(item.settings?.title ?? '');
            setNotes(item.settings?.notes ?? '');
            setDescription(item.settings?.description ?? '');
        },
        [setOpenEdit],
    );

    const handleMediaDownload = async (albumMedia: VaultMediaResponseList) => {
        if (!allowDownload || !albumMedia.full) {
            return;
        }

        // Add the media to the download queue
        addToQueue({
            subscriberId: subscriber._id!,
            username: subscriber.username,
            media: albumMedia,
        });

        enqueueSnackbar('Media added to download queue', { variant: 'success' });
    };

    return (
        <Box
            sx={{
                position: 'relative',
                cursor: 'pointer',
                width: { xs: '100%', sm: width },
                height: { xs: 'auto', sm: height },
                aspectRatio: { xs: '1 / 1', sm: 'auto' },
                borderRadius: 1.5,
                overflow: 'hidden',
                userSelect: 'none',
                outline: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid black',
                outlineOffset: -2,
                bgcolor: showImage ? 'background.default' : 'grey.200',
            }}
        >
            {/* Edit Dialog */}
            <DialogAdaptive open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
                <StyledCardTitleBar title="Edit media details" />
                <DialogContent sx={{ pt: 1, display: 'grid', gap: 2 }}>
                    <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} size="small" fullWidth />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    <TextField
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {!saveSettingsIsloading ? (
                        <>
                            <Button
                                onClick={e => {
                                    stop(e);
                                    setOpenEdit(false);
                                }}
                                color="inherit"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={async () => {
                                    if (!editItem) return;
                                    await onSaveSettings(editItem.id, { title, description, notes });
                                }}
                            >
                                Save
                            </Button>
                        </>
                    ) : (
                        <CircularProgress size={20} />
                    )}
                </DialogActions>
            </DialogAdaptive>
            {lightboxOpen && (
                <MediaLightbox
                    subscriber={subscriber}
                    open={lightboxOpen}
                    onClose={() => setLightboxOpen(false)}
                    item={item}
                    type={item.type}
                    title={item.type !== 'audio' ? `${item.id} - ${item?.full?.width}x${item?.full?.height}` : ''}
                />
            )}
            {!isDownloading && current && current.media.id === item.id && (
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        backgroundColor: theme.palette.grey[300],
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.secondary.main,
                        },
                    }}
                />
            )}
            {!isDownloading && current && current.media.id === item.id && (
                <Typography
                    component="div"
                    variant="body2"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        color: theme.palette.common.white,
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '100%',
                        pointerEvents: 'none',
                    }}
                >
                    {progress >= 0 ? `${progress}%` : 'Downloading...'}
                </Typography>
            )}
            {/* Media */}
            {showImage ? (
                <Box
                    component="img"
                    alt={item.type}
                    src={imgSrc}
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                    onError={e => {
                        (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }}
                    onClick={() => {
                        handleMediaClicked(item);
                    }}
                />
            ) : (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'grey.200',
                        color: 'text.secondary',
                    }}
                    onClick={() => {
                        handleMediaClicked(item);
                    }}
                >
                    <AudiotrackRoundedIcon fontSize="large" />
                </Box>
            )}
            {allowDownload !== undefined && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        alignItems: 'flex-end',
                    }}
                >
                    <Tooltip title={allowDownload ? 'Download' : 'Please Activate Sextforce To Download'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={e => {
                                    stop(e);

                                    if (item.full && item.full.url) {
                                        handleMediaDownload(item);
                                    } else {
                                        enqueueSnackbar('Media not available for download', { variant: 'warning' });
                                    }
                                }}
                                sx={{
                                    pointerEvents: 'auto',
                                    padding: 0.3,
                                    bgcolor: t => alpha(t.palette.grey[900], 0.5),
                                    '&:hover': { bgcolor: t => alpha(t.palette.grey[900], 0.9) },
                                }}
                                disabled={!allowDownload}
                            >
                                <DownloadRoundedIcon
                                    fontSize="small"
                                    sx={{
                                        color: 'common.white',
                                    }}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}
            {/* Top-left: View (photo) or Play (video/audio/gif) */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                }}
            >
                <Tooltip title={isPhoto ? 'View' : 'Play'}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={e => {
                                stop(e);
                                (e as any).stopImmediatePropagation?.();
                                if (item.full) {
                                    setLightboxOpen(true);
                                }
                            }}
                            sx={{
                                borderRadius: '12px',
                                pointerEvents: 'auto',
                                padding: 0.3,
                                bgcolor: t => alpha(t.palette.grey[900], 0.5),
                                '&:hover': { bgcolor: t => alpha(t.palette.grey[900], 0.9) },
                            }}
                        >
                            {isPhoto ? (
                                <VisibilityRoundedIcon
                                    fontSize="small"
                                    sx={{
                                        color: 'common.white',
                                    }}
                                />
                            ) : (
                                <>
                                    <PlayArrowRoundedIcon
                                        fontSize="small"
                                        sx={{
                                            color: 'common.white',
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.primary"
                                        fontSize={9}
                                        paddingRight="4px"
                                        sx={{ color: alpha('#fff', 0.9) }}
                                    >
                                        {durText}
                                    </Typography>
                                </>
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Footer overlay (title + date, pencil on right) */}
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2,
                    px: 1,
                    py: 0.25,
                    bgcolor: t => alpha(t.palette.grey[900], 0.5),

                    // ⬇️ two-row grid: title full width; date + actions share row 2
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gridTemplateRows: 'auto auto',
                    gridTemplateAreas: `"title title" "date actions"`,
                    alignItems: 'center',
                    columnGap: 8,
                    rowGap: 0,
                }}
            >
                {isSelected && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: `${alpha(theme.palette.primary.main, 0.5)}`,
                            zIndex: 9,
                            pointerEvents: 'none',
                        }}
                    />
                )}

                {/* Row 1: Title spans full width */}
                <Typography
                    variant="subtitle2"
                    sx={{
                        gridArea: 'title',
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: '10px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0, // critical for ellipsis in grid
                    }}
                    title={title}
                >
                    {/* {item.settings?.title || '\u00A0'} */}
                    {title}
                </Typography>

                {/* Row 2, left: Date */}
                <Typography
                    variant="caption"
                    sx={{
                        gridArea: 'date',
                        color: alpha('#fff', 0.9),
                        fontSize: '10px',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                >
                    {formatDate(item.createdAt)}
                </Typography>

                {/* Row 2, right: Edit icon */}
                {handleEditMenuOpen && (
                    <Box sx={{ gridArea: 'actions', justifySelf: 'end' }}>
                        <Tooltip title="Edit">
                            <span>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: 'common.white',
                                        padding: 0,
                                        '&:hover': { bgcolor: t => alpha(t.palette.background.paper, 0.6) },
                                    }}
                                    onClick={e => {
                                        stop(e);
                                        handleEditMenuOpen(item);
                                    }}
                                >
                                    <EditRoundedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default React.memo(
    VaultTile,
    (prev, next) => prev.item.id === next.item.id && prev.isSelected === next.isSelected && prev.item.settings === next.item.settings, // or a shallow compare if needed
);
